import { useEffect, useState } from "react";

import {
  setLogin,
  setRestored,
} from "../../api/redux-toolkit/reducer/authSlice";
import { fetchMe } from "../../api/users";
import { TOKEN_STORAGE_KEY } from "../../config/auth-storage-keys";
import { authStorage } from "../../config/session-storage";
import { restoreSessionAtBoot } from "../../services/session-boot";
import { reconcileStoredWorkFor } from "../../services/work-for-reconcile";
import { configureSessionRefresh } from "../../services/session-refresh";

import type { AuthUser } from "../../types/auth-types/AuthUser";
import { type AuthDispatch, useAuthDispatch } from "../../stores/auth-hooks";

interface SessionOutcome {
  accessToken: string;
  user: AuthUser;
}

// One profile overlay in flight at a time. The refresh handler below calls
// fetchMe(); a 401 on that call makes AxiosService run refreshSession(), whose
// success fires this same handler again. Skipping while one overlay is pending
// keeps that a single hop instead of a refresh loop that rotates the session.
let overlayInFlight = false;

/**
 * The shared demographic fields (name, phone, company, address) are overlaid
 * from the central directory ONLY by `GET /v1/users/me`; svc-02's exchange and
 * refresh responses carry the local row. The login callbacks already follow
 * the exchange with `fetchMe()` and a second `setLogin`. Boot restore and the
 * refresh loop used to stop after the first dispatch, so a new tab, a reload
 * and every token refresh replaced the overlaid user with the local row — and
 * dropped `assumptions`, which only `/me` returns.
 *
 * Order matters: the raw outcome is dispatched first so `restoring` ends and
 * the route guards can render; the overlay lands asynchronously and must never
 * hold the app back or sign the user out when the directory read fails.
 */
function applySessionOutcome(
  dispatch: AuthDispatch,
  outcome: SessionOutcome,
  isCancelled: () => boolean,
): void {
  dispatch(setLogin({ accessToken: outcome.accessToken, user: outcome.user }));
  if (overlayInFlight) return;
  overlayInFlight = true;
  void fetchMe()
    .then((me) => {
      if (isCancelled()) return;
      // Re-read the token: a refresh may have rotated it while /me was in
      // flight (never write the superseded one back), and a logout may have
      // cleared it (never sign a signed-out user back in with the old one).
      const accessToken = authStorage.getItem(TOKEN_STORAGE_KEY);
      if (!accessToken) return;
      dispatch(
        setLogin({ accessToken, user: me.user, assumptions: me.assumptions }),
      );
      // The stored Work-For target can have gone stale while this tab sat
      // open. Reads fall back to self server-side without telling the client,
      // so reconcile here rather than let the banner keep naming a customer
      // whose data is no longer being served.
      void reconcileStoredWorkFor();
    })
    .catch(() => {
      // Non-blocking: the exchange's user shape is the complete route-guard
      // shape, and the read path falls back to local columns by design.
    })
    .finally(() => {
      overlayInFlight = false;
    });
}

// The boot restore must resolve BEFORE the router renders, or ProtectedRoute
// evaluates against an empty in-memory token and redirects (ADR-0036). It runs
// in BOTH cookie modes (ADR-0038): with AUTH_COOKIE_SESSION off the cookie call
// is skipped inside, and the issuer re-exchange still restores the session.
export function SessionBoot({ children }: { children: React.ReactNode }) {
  const dispatch = useAuthDispatch();
  const [booted, setBooted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const isCancelled = () => cancelled;
    configureSessionRefresh((outcome) => {
      applySessionOutcome(dispatch, outcome, isCancelled);
    });
    void restoreSessionAtBoot()
      .then((outcome) => {
        if (cancelled) return;
        // A null outcome is the ordinary anonymous case: neither the cookie
        // session nor this tab's issuer token could restore anything.
        if (outcome) {
          applySessionOutcome(dispatch, outcome, isCancelled);
        } else {
          dispatch(setRestored());
        }
      })
      .catch(() => {
        // `restoring` must never be left true: both route guards render null
        // while it is, so a rejection here would blank the app for good.
        if (!cancelled) dispatch(setRestored());
      })
      .finally(() => {
        if (!cancelled) setBooted(true);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  if (!booted) return <div className="min-h-screen" aria-busy="true" />;
  return <>{children}</>;
}
